import { useCallback, useEffect, useRef, useState } from 'react';
import { X, AlertTriangle, HelpCircle } from 'lucide-react';
import { BROKERS, getBroker, BROKER_TONE } from '@/lib/brokers';

// Compact pill that displays the current broker for a position
// and (when `onClick`) opens the `BrokerModal` on click.
// `value` of null / undefined / '' renders as "Untagged".

export function BrokerBadge({ value, onClick, testId, size = 'sm' }) {
  const br = getBroker(value);
  const Icon = br?.icon || HelpCircle;
  const label = br?.short || 'Untagged';
  const tone = br ? (BROKER_TONE[br.key] || BROKER_TONE.other) : BROKER_TONE.other;
  const cls = size === 'xs'
    ? 'text-[10px] px-1.5 py-0.5'
    : 'text-[11px] px-2 py-0.5';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      title={onClick ? `Broker: ${label} (click to change)` : label}
      data-testid={testId}
      className={`inline-flex items-center gap-1 rounded-full ring-1 font-semibold whitespace-nowrap ${tone} ${cls} ${onClick ? 'hover:brightness-110 cursor-pointer' : 'cursor-default'}`}
    >
      <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

// Modal that lets the user pick a broker. Closing the modal
// (Esc / outside click / × button) persists the radio selection via
// onSave. When `ticker` is passed, a scope selector renders at the
// top so the user can choose "Change this Lot only" (default) or
// "Change all Lots for {ticker}" — and "Change EVERY lot" which gates
// behind a confirmation step (mirrors AccountTypeModal exactly).
//
// `value` of null / '' represents the Untagged state and is a valid
// save target — so users can clear the broker on a lot if they
// mis-tagged it.

const UNTAGGED_KEY = '__untagged__';

export default function BrokerModal({ open, currentValue, onSave, onClose, title = 'Set Broker', ticker }) {
  const wrapRef = useRef(null);
  const confirmRef = useRef(null);
  // Internal sentinel — radio value can't be empty string in HTML
  // forms cleanly, so we use a sentinel and convert to null on save.
  const selectedRef = useRef(currentValue || UNTAGGED_KEY);
  const scopeRef = useRef('lot');
  const [confirmPortfolio, setConfirmPortfolio] = useState(false);

  useEffect(() => {
    if (open) {
      selectedRef.current = currentValue || UNTAGGED_KEY;
      scopeRef.current = 'lot';
      setConfirmPortfolio(false);
    }
  }, [open, currentValue]);

  // Round P108v (Feb 2026) — auto-scroll to the Yes/Cancel panel
  // the moment it appears so users don't miss it below the long
  // broker list.
  useEffect(() => {
    if (confirmPortfolio && confirmRef.current) {
      confirmRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [confirmPortfolio]);

  const handleClose = useCallback(() => {
    const chosen = selectedRef.current === UNTAGGED_KEY ? null : selectedRef.current;
    const scope = scopeRef.current;
    if (scope === 'portfolio' && !confirmPortfolio) {
      setConfirmPortfolio(true);
      return;
    }
    const wasUntagged = !currentValue;
    const changed = (chosen || null) !== (currentValue || null);
    if (scope === 'all' || scope === 'portfolio' || changed) {
      // For "all"/"portfolio" scope we emit even when the value
      // didn't change (lets the user bulk-clear or bulk-set).
      // Skip the no-op single-lot case to avoid useless API noise.
      if (scope === 'lot' && !changed && !wasUntagged) {
        // unchanged single-lot save — skip
      } else {
        onSave?.(chosen, { scope });
      }
    }
    onClose?.();
  }, [onSave, onClose, currentValue, confirmPortfolio]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) handleClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, handleClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="broker-modal">
      <div
        ref={wrapRef}
        // Phone fix (Feb 2026): extra bottom padding so the
        // confirmation buttons can scroll well clear of the floating
        // Emergent dev/deploy badge that sits in the lower-right
        // corner of mobile viewports. Desktop keeps the original
        // tight padding via the sm: prefix.
        className="bg-card border border-border rounded-xl shadow-xl max-w-md w-full p-4 pb-24 sm:pb-4 max-h-[90vh] overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold">{title}</h3>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 rounded-md hover:bg-muted text-muted-foreground"
            data-testid="broker-modal-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3 leading-snug">
          Tag this lot with the brokerage where it lives. Used to roll up
          your portfolio per-broker on the Tax Account Audit. Optional —
          leave Untagged if you don&rsquo;t want to track this. The selected
          option saves when you close this modal.
        </p>
        {ticker && (
          <fieldset
            className="mb-3 pb-3 border-b border-border space-y-1.5"
            data-testid="broker-scope"
          >
            <legend className="sr-only">Apply to</legend>
            <ScopeRow
              value="lot"
              label="Change this Lot only"
              hint={`Update just this single purchase of ${ticker}.`}
              defaultChecked
              onSelect={(v) => { scopeRef.current = v; }}
            />
            <ScopeRow
              value="all"
              label={`Change ALL ${ticker} lots`}
              hint={`Tag every lot of ${ticker} in your portfolio at once. Handy if you keep the whole ticker in one brokerage.`}
              onSelect={(v) => { scopeRef.current = v; }}
            />
            <ScopeRow
              value="portfolio"
              label="Change EVERY lot in my portfolio"
              hint="One-click bulk tag for users who keep everything at a single brokerage. Confirmation required."
              danger
              onSelect={(v) => { scopeRef.current = v; }}
            />
          </fieldset>
        )}
        <fieldset className="space-y-1.5">
          <legend className="sr-only">Broker</legend>
          <BrokerRow
            br={{ key: UNTAGGED_KEY, label: 'Untagged', icon: HelpCircle }}
            initialChecked={(currentValue || UNTAGGED_KEY) === UNTAGGED_KEY}
            onSelect={(k) => { selectedRef.current = k; }}
            hint="No brokerage tag. Lot still appears in your portfolio; it just won't roll up under a specific broker on the audit page."
          />
          {BROKERS.map((br) => (
            <BrokerRow
              key={br.key}
              br={br}
              initialChecked={br.key === currentValue}
              onSelect={(k) => { selectedRef.current = k; }}
            />
          ))}
        </fieldset>
        {confirmPortfolio && (
          <div ref={confirmRef} className="mt-3 pt-3 border-t-2 border-rose-300 dark:border-rose-700" data-testid="broker-portfolio-confirm">
            <div className="flex items-start gap-2 p-3 rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-300 dark:border-rose-700">
              <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-300 shrink-0 mt-0.5" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-rose-900 dark:text-rose-200 mb-1">
                  Tag every lot in your entire portfolio?
                </div>
                <p className="text-[11px] text-rose-800 dark:text-rose-300/90 leading-snug mb-2">
                  This will set <strong>every position across every ticker</strong>{' '}
                  to the broker you selected above. There&rsquo;s no undo
                  beyond manually re-tagging lots. Use this only if you keep
                  your whole portfolio at a single brokerage.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const chosen = selectedRef.current === UNTAGGED_KEY ? null : selectedRef.current;
                      onSave?.(chosen, { scope: 'portfolio' });
                      onClose?.();
                    }}
                    className="px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold"
                    data-testid="broker-portfolio-confirm-yes"
                  >
                    Yes, tag everything
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      scopeRef.current = 'lot';
                      setConfirmPortfolio(false);
                    }}
                    className="px-3 py-1.5 rounded-md border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 text-xs font-semibold hover:bg-rose-100 dark:hover:bg-rose-900/40"
                    data-testid="broker-portfolio-confirm-no"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BrokerRow({ br, initialChecked, onSelect, hint }) {
  const Icon = br.icon;
  return (
    <label
      className="flex items-start gap-2 p-2 rounded-md border border-border hover:border-primary/50 hover:bg-muted/40 cursor-pointer"
      data-testid={`broker-option-${br.key}`}
    >
      <input
        type="radio"
        name="broker"
        value={br.key}
        defaultChecked={initialChecked}
        onChange={() => onSelect(br.key)}
        className="mt-1 accent-primary"
        data-testid={`broker-radio-${br.key}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-sm font-semibold">
          <Icon className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
          <span>{br.label}</span>
        </div>
        {hint && (
          <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">{hint}</p>
        )}
      </div>
    </label>
  );
}

function ScopeRow({ value, label, hint, defaultChecked, onSelect, danger }) {
  const ringCls = danger
    ? 'hover:bg-rose-50 dark:hover:bg-rose-950/30'
    : 'hover:bg-muted/40';
  const labelCls = danger
    ? 'text-rose-700 dark:text-rose-300'
    : '';
  return (
    <label
      className={`flex items-start gap-2 p-1.5 rounded-md cursor-pointer ${ringCls}`}
      data-testid={`broker-scope-${value}`}
    >
      <input
        type="radio"
        name="broker-scope"
        value={value}
        defaultChecked={defaultChecked}
        onChange={() => onSelect(value)}
        className="mt-1 accent-primary"
        data-testid={`broker-scope-radio-${value}`}
      />
      <div className="flex-1 min-w-0">
        <div className={`text-[12px] font-semibold leading-tight ${labelCls}`}>{label}</div>
        <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">{hint}</p>
      </div>
    </label>
  );
}
